import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { getSupabase } from './supabase-client';
import type { AuthRole, AuthSession, AuthUser } from './types';

interface AuthContextValue {
  session: AuthSession | null;
  user: AuthUser | null;
  loading: boolean;
  signInWithMagicLink: (email: string) => Promise<{ error: Error | null }>;
  signInWithPassword: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface SupabaseSessionLike {
  user: {
    id: string;
    email?: string;
    app_metadata: Record<string, unknown>;
  };
  access_token: string;
  refresh_token: string;
  expires_at?: number;
}

function mapSession(s: SupabaseSessionLike): AuthSession {
  return {
    user: {
      id: s.user.id,
      email: s.user.email ?? '',
      role: (s.user.app_metadata['role'] as AuthRole) ?? 'customer',
      app_metadata: s.user.app_metadata,
    },
    access_token: s.access_token,
    refresh_token: s.refresh_token,
    expires_at: s.expires_at ?? 0,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const supabase = getSupabase();

    // 1. Restore session from storage on mount
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session ? mapSession(data.session) : null);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });

    // 2. Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess ? mapSession(sess) : null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const value: AuthContextValue = {
    session,
    user: session?.user ?? null,
    loading,
    signInWithMagicLink: async (email) => {
      const { error } = await getSupabase().auth.signInWithOtp({
        email,
        options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
      });
      return { error: error as Error | null };
    },
    signInWithPassword: async (email, password) => {
      const { error } = await getSupabase().auth.signInWithPassword({ email, password });
      return { error: error as Error | null };
    },
    signInWithGoogle: async () => {
      const { error } = await getSupabase().auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${window.location.origin}/auth/callback` },
      });
      return { error: error as Error | null };
    },
    signOut: async () => {
      await getSupabase().auth.signOut();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
